import { NEXT_OPERATIONS, type NextOperation } from './lists.js';
import type { VoyageReportKind } from './schemas.js';
import {
  ENDS,
  type Family,
  replay,
  type SequenceIssue,
  type SequenceReport,
  STARTS,
} from './sequence.js';

/**
 * Next Operation durum makinesi (ortağın `g5.jpeg` diyagramı ve cevabı #6 "zorunlu"; ADR 0057 §5).
 * Her akış raporu sonunda sonraki işlem seçilir; seçim sıradaki raporun türünü belirler, başka tür
 * açılmaz. Yan olaylar (yük, kiralama) akışın dışındadır. `sequence.ts`'in çift kuralları aynen
 * geçerlidir; bu, onların üstünde daha dar bir kuraldır. Saf fonksiyonlar.
 */

/** Akışın dışındaki olaylar: her zaman girilir, seçim taşımaz, bekleyen seçimi değiştirmez. */
export const SIDE_KINDS: ReadonlySet<VoyageReportKind> = new Set([
  'cargo_start',
  'cargo_complete',
  'on_hire',
  'off_hire',
]);

/** Kalkıştan sonra (g5 "Kalkış sonrası": COSP, beklemede kal: Anchor / Drift / Orders). */
const AFTER_DEPARTURE: readonly NextOperation[] = [
  'sea_passage',
  'waiting_orders',
  'anchorage',
  'drifting',
  // Limandan doğrudan boğaz/kanala çıkış ve yanaşma yeri değiştirme (ajan kararı, ADR 0057 §5)
  'strait_canal',
  'berth',
];

/**
 * Türe göre seçenekler (g5.jpeg). Karar noktaları birden çok seçenek sunar; aşama içi raporların
 * tek seçeneği aşamanın devamıdır (form kendiliğinden seçer).
 */
export const NEXT_OPERATION_OPTIONS: Readonly<Record<VoyageReportKind, readonly NextOperation[]>> =
  {
    departure: AFTER_DEPARTURE,
    cosp: ['sea_passage'],
    noon: ['sea_passage'],
    // EOSP sonrası ne yapacaksınız: 5A Berth, 5B Anchorage, 5C Drifting, 5D Canal/Strait, 5E COSP
    eosp: ['berth', 'anchorage', 'drifting', 'strait_canal', 'sea_passage'],
    // Berthing ve limanda günlük: limanda kal (Port Daily) ya da kalkış (ADR 0059 §2; iki ayrı
    // seçenek ortak geri bildirimi 2026-10-19, ADR 0083 Ek)
    arrival: ['port_stay', 'departure'],
    port_daily: ['port_stay', 'departure'],
    anchorage_start: ['anchorage'],
    anchorage_daily: ['anchorage'],
    // Anchor Up / Drift End: bundan sonra yanaşma, kanal/boğaz ya da COSP
    anchorage_end: ['berth', 'strait_canal', 'sea_passage'],
    waiting_start: ['waiting_orders'],
    waiting_end: ['sea_passage', 'anchorage', 'drifting', 'strait_canal', 'berth'],
    drift_start: ['drifting'],
    drift_daily: ['drifting'],
    drift_end: ['berth', 'strait_canal', 'sea_passage'],
    strait_entry: ['strait_canal'],
    // Geçiş sonrası ne olacak: yanaşma, demirleme, drift, COSP
    strait_exit: ['berth', 'anchorage', 'drifting', 'sea_passage'],
    canal_entry: ['strait_canal'],
    canal_exit: ['berth', 'anchorage', 'drifting', 'sea_passage'],
    cargo_start: [],
    cargo_complete: [],
    on_hire: [],
    off_hire: [],
  };

export function nextOperationOptions(kind: VoyageReportKind): readonly NextOperation[] {
  return NEXT_OPERATION_OPTIONS[kind] ?? [];
}

const isOperation = (value: unknown): value is NextOperation =>
  typeof value === 'string' && (NEXT_OPERATIONS as readonly string[]).includes(value);

/** Raporun seçimi; türün seçeneklerinden değilse yok sayılır. */
function selectedOf(report: SequenceReport): NextOperation | null {
  const value = report.data.nextOperation;
  return isOperation(value) && nextOperationOptions(report.kind).includes(value) ? value : null;
}

/** Seyir açıkken Sea Passage devamı: Noon, EOSP ve seyir içi girişler (CP kuralları 7, 10, 11). */
const IN_PASSAGE: readonly VoyageReportKind[] = [
  'noon',
  'eosp',
  'strait_entry',
  'canal_entry',
  'anchorage_start',
  'drift_start',
  'waiting_start',
];

/**
 * İşlemden sonra girilebilecek türler; açık çiftlere göre (girişten sonra çıkış). Berthing'in
 * `departure` seçimi her limanda Departure'ı açar (ADR 0058 §4; ADR 0057 §5'teki geçici kural
 * kalktı).
 */
export function allowedKindsFor(
  operation: NextOperation,
  open: ReadonlySet<Family>,
): VoyageReportKind[] {
  switch (operation) {
    case 'sea_passage':
      return open.has('passage') ? [...IN_PASSAGE] : ['cosp'];
    case 'berth':
      return ['arrival'];
    case 'anchorage':
      return open.has('anchorage') ? ['anchorage_daily', 'anchorage_end'] : ['anchorage_start'];
    case 'drifting':
      return open.has('drift') ? ['drift_daily', 'drift_end'] : ['drift_start'];
    case 'waiting_orders':
      return open.has('waiting') ? ['waiting_end'] : ['waiting_start'];
    case 'strait_canal':
      if (open.has('strait')) return ['strait_exit'];
      if (open.has('canal')) return ['canal_exit'];
      return ['strait_entry', 'canal_entry'];
    case 'departure':
      // Limandaki gemi: kalkış ya da limanda günlük (ADR 0059 §2)
      return ['departure', 'port_daily'];
    case 'port_stay':
      // Limanda kal: önce günlük; kalkış saati kesin olmadığından kalkış da açık (ADR 0083 Ek)
      return ['port_daily', 'departure'];
  }
}

interface Walk {
  pending: NextOperation | null;
  open: ReadonlySet<Family>;
}

/** Raporları sırayla yürür; her akış raporunda o anki bekleyen seçimi denetleyiciye verir. */
function walk(
  reports: readonly SequenceReport[],
  visit?: (report: SequenceReport, state: Walk) => void,
): Walk {
  let pending: NextOperation | null = null;
  let open = new Set<Family>();
  for (const step of replay(reports)) {
    const { report } = step;
    // Son raporun açtığı ya da kapattığı çift de sonuca girsin: durum rapordan sonrası
    open = afterReport(step.open, report);
    if (SIDE_KINDS.has(report.kind)) continue;
    visit?.(report, { pending, open: step.open });
    pending = selectedOf(report);
  }
  return { pending, open };
}

/** Raporun çift durumuna etkisi (`replay` ile aynı kural). */
function afterReport(before: ReadonlySet<Family>, report: SequenceReport): Set<Family> {
  const open = new Set(before);
  const starts = STARTS[report.kind];
  const ends = ENDS[report.kind];
  if (starts) open.add(starts);
  else if (ends) open.delete(ends);
  else if (report.kind === 'noon') open.add('passage');
  else if (report.kind === 'anchorage_daily') open.add('anchorage');
  else if (report.kind === 'drift_daily') open.add('drift');
  return open;
}

/**
 * Bütün dizide Next Operation kilidi: her akış raporu, kendinden önceki son akış raporunun
 * seçimine uymalı. Seçimi olmayan (eski ya da taslak) rapor kısıtlamaz; seferin ilk raporu serbest
 * (ADR 0047 §2).
 */
export function checkNextOperations(reports: readonly SequenceReport[]): SequenceIssue[] {
  const issues: SequenceIssue[] = [];
  walk(reports, (report, state) => {
    if (state.pending === null) return;
    if (!allowedKindsFor(state.pending, state.open).includes(report.kind)) {
      issues.push({ id: report.id, code: 'NEXT_OPERATION' });
    }
  });
  return issues;
}

/**
 * Yazma anındaki kilit (ADR 0058 §4): adayın (yeni ya da güncellenen rapor) getirdiği çelişkiler.
 * Aday olmadan da var olan çelişki (kural değişmeden önce yazılmış eski sefer) yazmayı durdurmaz;
 * adayın kendisi ya da kendinden sonraki raporu bozması durdurur.
 */
export function introducedNextOperationIssues(
  others: readonly SequenceReport[],
  candidate: SequenceReport,
): SequenceIssue[] {
  const rest = others.filter((report) => report.id !== candidate.id);
  const before = new Set(checkNextOperations(rest).map((i) => `${i.id}|${i.code}`));
  return checkNextOperations([...rest, candidate]).filter((i) => !before.has(`${i.id}|${i.code}`));
}

/**
 * Seferin sonuna eklenecek yeni raporun girilebilecek türleri ("Add Event" ve "Next Report").
 * Bekleyen seçim yoksa `null` (kısıt yok). Yan olaylar her zaman listededir.
 */
export function allowedNextKinds(
  reports: readonly SequenceReport[],
): { operation: NextOperation; kinds: VoyageReportKind[] } | null {
  const state = walk(reports);
  if (state.pending === null) return null;
  return {
    operation: state.pending,
    kinds: [...allowedKindsFor(state.pending, state.open), ...SIDE_KINDS],
  };
}

/**
 * Gönderimde seçim zorunlu (ortağın cevabı #6): akış raporunda seçim yoksa ya da türün
 * seçeneklerinden değilse `true`. Yan olaylarda her zaman `false`.
 */
export function nextOperationMissing(
  kind: VoyageReportKind,
  data: Readonly<Record<string, unknown>>,
): boolean {
  const options = nextOperationOptions(kind);
  if (options.length === 0) return false;
  const value = data.nextOperation;
  return !isOperation(value) || !options.includes(value);
}

/**
 * Her akış raporu için önerilen seçim: kendisinden sonraki akış raporunu açan seçenek; sonraki
 * yoksa ya da hiçbiri uymuyorsa ilk seçenek. Formun varsayılanı ve tanıtım verisi için (ADR 0057
 * §5); kayıtta seçim kullanıcınındır.
 */
export function suggestNextOperations(
  reports: readonly SequenceReport[],
): Map<string, NextOperation> {
  const flow: { report: SequenceReport; open: Set<Family> }[] = [];
  for (const step of replay(reports)) {
    if (SIDE_KINDS.has(step.report.kind)) continue;
    flow.push({ report: step.report, open: afterReport(step.open, step.report) });
  }
  const suggested = new Map<string, NextOperation>();
  flow.forEach(({ report, open }, index) => {
    const options = nextOperationOptions(report.kind);
    const next = flow[index + 1]?.report.kind;
    // Önce sıradaki raporu ilk sırada açan seçenek (limanda: günlük → `port_stay`, kalkış →
    // `departure`), yoksa onu açan herhangi biri
    const fit = next
      ? (options.find((option) => allowedKindsFor(option, open)[0] === next) ??
        options.find((option) => allowedKindsFor(option, open).includes(next)))
      : undefined;
    const choice = fit ?? options[0];
    if (choice) suggested.set(report.id, choice);
  });
  return suggested;
}
